/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { restartableTask, timeout } from 'ember-concurrency';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type StoreService from 'volta/services/store';

interface ITokenFilterArgs extends IFilterArgs<{
  options?: string[],
  searchFn?: (query: object, filterValues: TFilterValues) => Promise<string[]>
}> {
  options?: string[];
  warehouseIds?: string[] | string;
  renderInplace?: string;
  horizontalPosition?: 'left' | 'right' | 'auto';
  matchTriggerWidth?: boolean;
  label?: string;
  placeholder?: string;
  noMatchesMessage?: string;
  searchFn?: (query: object, filterValues: TFilterValues) => Promise<string[]>
}

interface ITokenFilterSignature {
  Args: ITokenFilterArgs;
}

export default class FiltersTokenFilterComponent extends Component<ITokenFilterSignature> {
  // Services
  // ~~~~~

  @service store!: StoreService;

  // Properties
  // ~~~~~

  @tracked options = this.args.componentArgs?.options ?? [];

  // Computed Properties
  // ~~~~~

  get values() {
    return this.args.values ?? [];
  }

  get searchFn() {
    const { componentArgs, searchFn } = this.args;
    return componentArgs?.searchFn ?? searchFn;
  }

  get warehouseIds(): string[] {
    const { warehouseIds, filterValues = {} } = this.args;
    if (warehouseIds && warehouseIds.length > 0) {
      return typeof warehouseIds === 'string' ? warehouseIds.split(',') : warehouseIds;
    } else {
      return (filterValues['warehouse']?.values as string[]) ?? [];
    }
  }

  // Lifecycle Hooks
  // ~~~~~

  // Actions
  // ~~~~~

  @action
  valuesDidChange(values: string[]) {
    return this.args.onChange?.(values);
  }

  @action
  singleValueDidChange(option?: string) {
    this.args.onChange?.(option ? [option] : []);
  }

  // Methods
  // ~~~~~
  searchTask = restartableTask(async (term?: string) => {
    await timeout(600);
    const { warehouseIds, searchFn } = this;
    const query: Record<string, any> = { search: term, pageSize: 100 };

    if (warehouseIds.length > 0) {
      query['warehouses'] = warehouseIds.join(',');
    }

    const items: string[] =
      typeof searchFn === 'function' ? await searchFn(query, this.args.filterValues) : [];
    this.options = items;
    return items;
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::TokenFilter': typeof FiltersTokenFilterComponent;
    'filters/token-filter': typeof FiltersTokenFilterComponent;
  }
}
